/**
 * Metrics Dashboard - Enterprise Grade
 * Quality metrics and KPI tracking
 */

import React, { useState, useEffect } from 'react';
import {
  Target, TrendingUp, TrendingDown, BarChart3, Download, RefreshCw,
  Award, AlertTriangle, CheckCircle, Activity, ArrowUp, ArrowDown
} from 'lucide-react';
import { qualityMetrics } from '../../data/mockData';
import { useAppContext } from '../../context/AppContext';

const MetricsDashboard = () => {
  const { state } = useAppContext();
  const selectedFacility = state.selectedFacility;
  
  const [metrics, setMetrics] = useState({});
  const [loading, setLoading] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState(null);

  useEffect(() => {
    loadData();
  }, [selectedFacility]);

  const loadData = () => {
    setLoading(true);
    // In production, this would fetch from service
    setMetrics(qualityMetrics);
    setLoading(false);
  };

  const calculateOverallScore = () => {
    const metricValues = Object.values(metrics);
    if (metricValues.length === 0) return 0;
    const totalScore = metricValues.reduce((sum, m) => sum + m.current, 0);
    const maxScore = metricValues.reduce((sum, m) => sum + m.benchmark, 0);
    return Math.round((totalScore / maxScore) * 100);
  };

  const overallScore = calculateOverallScore();

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3 mb-2">
              <Target className="w-8 h-8" />
              Quality Metrics
            </h1>
            <p className="text-emerald-100 text-lg">Key performance indicators and quality tracking</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={loadData}
              className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg font-semibold flex items-center gap-2 transition-all"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button
              onClick={() => setShowExportModal(true)}
              className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg font-semibold flex items-center gap-2 transition-all"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>

        {/* Overall Score */}
        <div className="bg-white bg-opacity-10 rounded-lg p-4 backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-emerald-100 mb-1">Overall Quality Score</div>
              <div className="text-4xl font-bold">{overallScore}%</div>
            </div>
            <div className="text-right">
              <div className="text-sm text-emerald-100 mb-1">Status</div>
              <div className="text-lg font-semibold">
                {overallScore >= 95 ? 'Excellent' : overallScore >= 85 ? 'Good' : 'Needs Improvement'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Object.entries(metrics).map(([key, metric]) => {
          const isImproving = metric.trend < 0; // Negative trend is improvement for these metrics
          const performance = ((metric.target - metric.current) / metric.target) * 100;
          const isAboveTarget = metric.current <= metric.target;
          
          return (
            <div
              key={key}
              className="bg-white rounded-xl p-6 shadow-lg border-2 border-gray-200 hover:border-emerald-300 transition-all cursor-pointer"
              onClick={() => setSelectedMetric({ key, ...metric })}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-emerald-100 rounded-xl">
                  <Target className="w-6 h-6 text-emerald-600" />
                </div>
                <div className="flex items-center gap-1">
                  {isImproving ? (
                    <TrendingDown className="w-5 h-5 text-green-600" />
                  ) : (
                    <TrendingUp className="w-5 h-5 text-red-600" />
                  )}
                  <span className={`text-sm font-semibold ${isImproving ? 'text-green-600' : 'text-red-600'}`}>
                    {Math.abs(metric.trend)}%
                  </span>
                </div>
              </div>
              
              <h3 className="font-bold text-gray-800 mb-2 capitalize text-lg">
                {key.replace(/([A-Z])/g, ' $1').trim()}
              </h3>
              
              <div className="space-y-3">
                <div>
                  <div className="flex items-end gap-2 mb-1">
                    <div className={`text-3xl font-bold ${isAboveTarget ? 'text-green-600' : 'text-red-600'}`}>
                      {metric.current}
                    </div>
                    <div className="text-sm text-gray-500 mb-1">per 1,000</div>
                  </div>
                  <div className="text-sm text-gray-600">Target: {metric.target}</div>
                  <div className="text-xs text-gray-500">Benchmark: {metric.benchmark}</div>
                </div>

                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${isAboveTarget ? 'bg-green-600' : 'bg-red-600'}`}
                    style={{ width: `${Math.min((metric.current / metric.target) * 100, 100)}%` }}
                  ></div>
                </div>

                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600">Performance</span>
                  <span className={`font-semibold ${isAboveTarget ? 'text-green-600' : 'text-red-600'}`}>
                    {performance.toFixed(1)}% {isAboveTarget ? 'above' : 'below'} target
                  </span>
                </div>

                {metric.improvement && (
                  <div className="text-xs text-green-600 font-semibold bg-green-50 p-2 rounded">
                    {metric.improvement}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Trend Visualization */}
      <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
        <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
          <BarChart3 className="w-6 h-6 text-emerald-600" />
          Performance Trends
        </h3>
        <div className="h-64 flex items-end justify-between gap-2">
          {Object.entries(metrics).map(([key, metric]) => {
            const height = ((metric.target - metric.current) / metric.target) * 100;
            return (
              <div key={key} className="flex-1 flex flex-col items-center group">
                <div
                  className={`w-full rounded-t-lg transition-all cursor-pointer group-hover:opacity-80 ${
                    metric.current <= metric.target ? 'bg-gradient-to-t from-green-500 to-green-400' : 'bg-gradient-to-t from-red-500 to-red-400'
                  }`}
                  style={{ height: `${Math.max(height, 10)}%` }}
                  title={`${key}: ${metric.current} (Target: ${metric.target})`}
                ></div>
                <div className="text-xs text-gray-600 mt-2 text-center">
                  {key.substring(0, 4)}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Metric Detail Modal */}
      {selectedMetric && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800 capitalize">
                {selectedMetric.key.replace(/([A-Z])/g, ' $1').trim()} - Detailed View
              </h2>
              <button
                onClick={() => setSelectedMetric(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-emerald-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">Current Rate</div>
                  <div className="text-3xl font-bold text-emerald-600">{selectedMetric.current}</div>
                  <div className="text-xs text-gray-500">per 1,000 resident days</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">Target</div>
                  <div className="text-3xl font-bold text-green-600">{selectedMetric.target}</div>
                  <div className="text-xs text-gray-500">Target rate</div>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">Benchmark</div>
                  <div className="text-2xl font-bold text-blue-600">{selectedMetric.benchmark}</div>
                  <div className="text-xs text-gray-500">Industry benchmark</div>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">Trend</div>
                  <div className={`text-2xl font-bold ${selectedMetric.trend < 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {selectedMetric.trend < 0 ? '↓' : '↑'} {Math.abs(selectedMetric.trend)}%
                  </div>
                  <div className="text-xs text-gray-500">Change from last period</div>
                </div>
              </div>

              {selectedMetric.improvement && (
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <div className="font-semibold text-green-800 mb-1">Improvement</div>
                  <div className="text-sm text-green-700">{selectedMetric.improvement}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <Download className="w-6 h-6 text-emerald-600" />
                Export Metrics
              </h2>
              <button
                onClick={() => setShowExportModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={() => {
                  const csv = generateCSV();
                  downloadFile(csv, `quality-metrics-${selectedFacility}.csv`, 'text/csv');
                  setShowExportModal(false);
                }}
                className="w-full px-4 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-semibold flex items-center justify-center gap-2"
              >
                <Download className="w-5 h-5" />
                Export as CSV
              </button>
              <button
                onClick={() => {
                  const json = JSON.stringify(metrics, null, 2);
                  downloadFile(json, `quality-metrics-${selectedFacility}.json`, 'application/json');
                  setShowExportModal(false);
                }}
                className="w-full px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-semibold flex items-center justify-center gap-2"
              >
                <Download className="w-5 h-5" />
                Export as JSON
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  function generateCSV() {
    const headers = ['Metric', 'Current', 'Target', 'Benchmark', 'Trend', 'Improvement'];
    const rows = Object.entries(metrics).map(([key, metric]) => [
      key,
      metric.current,
      metric.target,
      metric.benchmark,
      metric.trend,
      metric.improvement || 'N/A'
    ].join(','));
    return [headers.join(','), ...rows].join('\n');
  }

  function downloadFile(content, filename, mimeType) {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

export default MetricsDashboard;
